/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What the runtime does without being asked, on every mapping surface** (`#1233`, `XS3` slice 6).
 *
 * Reported by Todd 2026-09-03, rehearsing slice 5: *"The 'we already do this' section is chips,
 * appears different from how we show the same concept for outbound mapping (we send
 * automatically)."*
 *
 * Both sides answer the identical operator question — **do I need a row for this?** — off the
 * identical kind of declaration: `AUTO_POPULATED_PARAM_NAMES` outbound, `DRIVER_SUPPLIED_TARGETS`
 * inbound. Outbound it was a collapsible panel with an *edit to override* affordance (`#951 · R5`);
 * inbound it was a line of chips with no affordance at all. Nothing checked that they meant the same
 * thing, and an operator who had learned the panel did not recognise the chips.
 *
 * It is *shared logic is not shared words* (`#1187`) one level up — the two surfaces share the
 * **decision** and diverged on the **rendering** — and the answer is the same: the thing to share is
 * the pattern, not the CSS.
 *
 * ## What this owns and what it does not
 *
 * It owns the **affordance**: a collapsed-by-default disclosure whose header states the claim and
 * the count, an action hint that says what to *do* rather than what this is, and an optional caveat
 * above the rows. It does **not** own the rows — the outbound body editor groups them by ORTB
 * section and the parameter editor lists them flat with a seed value, and forcing those into one
 * shape would be a rewrite of two working surfaces to satisfy a rule about a third.
 *
 * ## Closed by default, deliberately
 *
 * *These happen whether or not you author them* is news exactly once, when the set changes. A panel
 * that re-announces itself on every visit is one an operator learns to collapse without reading,
 * which is the same failure as a gate whose normal output is noise.
 */

import React from 'react';
import { Box, HStack, Text, VisuallyHidden, VStack } from '@chakra-ui/react';

export interface AutomaticPanelProps {
  /** The claim, in the surface's own words — *Sent automatically* · *Extracted automatically*. */
  title: string;
  /** How many. Rendered beside the title, because *is this a long list* decides whether to open it. */
  count: number;
  /**
   * What to **do** about a row, not what the panel is (Todd, `#951`): *edit to override or suppress*.
   * `null` where the surface offers no action — and a surface that offers none should say why on the
   * rows rather than leaving the header silent.
   */
  action: string | null;
  /** A sentence above the rows, where the claim needs one. `null` for none. */
  caveat?: React.ReactNode;
  /**
   * **A change here re-opens the panel** — a signal, not a controlled `open` boolean.
   *
   * The boolean is wrong in a way that is easy to miss: a caller sets it true when something
   * material changes, the operator collapses the panel by hand, and the next *material change*
   * passes the **same** `true` — so nothing re-fires and the panel stays shut exactly when it has
   * news. A value that differs from the last one cannot have that problem.
   */
  reopenKey?: string | number;
  children: React.ReactNode;
}

export const AutomaticPanel: React.FC<AutomaticPanelProps> = ({
  title,
  count,
  action,
  caveat = null,
  reopenKey,
  children,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  // A caller bumping the key is saying *this set just changed*, which is the one moment the claim is
  // news again. The first render is not one of those moments, so it does not open on mount.
  const seen = React.useRef(reopenKey);
  React.useEffect(() => {
    if (reopenKey !== seen.current) {
      seen.current = reopenKey;
      setIsOpen(true);
    }
  }, [reopenKey]);

  if (count <= 0) return null;

  return (
    <Box borderWidth="1px" borderColor="border.default" borderRadius="8px" bg="bg.subtle" overflow="hidden">
      <HStack
        as="button"
        w="100%"
        px={3}
        py={2}
        gap={2}
        textAlign="left"
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
        _hover={{ bg: 'bg.canvas' }}
      >
        <Text fontSize="xs" color="fg.muted" fontWeight="700" w="10px">
          {isOpen ? '▾' : '▸'}
        </Text>
        <Text fontSize="xs" fontWeight="600" color="fg.default">
          {title}
        </Text>
        <Text fontSize="xs" color="fg.muted">
          · {count}
        </Text>
        {action && (
          <Text fontSize="xs" color="fg.muted" ml="auto" pr={1}>
            {action}
          </Text>
        )}
      </HStack>
      {isOpen && (
        <VStack align="stretch" gap={0} borderTopWidth="1px" borderColor="border.default">
          {caveat && (
            <Text fontSize="xs" color="fg.muted" px={3} py={2} bg="bg.canvas">
              {caveat}
            </Text>
          )}
          {children}
        </VStack>
      )}
    </Box>
  );
};

/**
 * One ghosted row — the shape both surfaces already use.
 *
 * **Ghosted throughout** (`fg.subtle`), so the eye reads the authored rows above as the operator's
 * and these as the system's. That distinction is the panel's whole job: without it an automatic
 * field reads as one somebody configured and forgot.
 */
export const AutomaticRow: React.FC<{
  name: string;
  detail: string;
  /**
   * **What this field is, beyond what the driver does with it** — the grain mark (`X32` Q3,
   * `#1259`).
   *
   * Required and nullable rather than optional: a surface with nothing to say has to **say so**, and
   * an optional prop through a shared row is the one that renders nothing on the surface that
   * forgot it. `null` is a declined mark, and it is the honest answer on a surface whose
   * destination is a key on a wire.
   */
  mark: { label: string; detail?: string } | null;
  /**
   * **What the field holds in the sample on screen** (`#1144`), one value per ad. Required and
   * nullable for the reason `mark` is: `null` is a surface with no sample to read, and a surface that
   * forgot would otherwise render nothing and look like one that had nothing to say.
   */
  resolution: { statement: string; detail: string; quiet: boolean } | null;
  action?: React.ReactNode;
}> = ({ name, detail, mark, resolution, action }) => (
  /* **Centred, because the row's trailing control is taller than its text** (`#1259`, Todd
     2026-09-08). It was `align="flex-start"`, which top-aligns every child's **box** — so a 24px
     `edit` button centred its own label while the name, the detail and the mark sat on the row's
     first line, and the button read as dropped by about half a line on every row that has one.

     `center` also settles a disagreement rather than making a choice: `RequestParameterEditor`
     hand-rolls this same row inline and has always used the default, which is `center`. Two
     renderings of one row differing on alignment is the visible half of their being two — the copy
     is worth collapsing into this component, and that is its own change. */
  <HStack px={3} py={1.5} gap={3} _hover={{ bg: 'bg.canvas' }} align="center">
    <Text fontFamily="mono" fontSize="sm" color="fg.subtle" minW="150px" maxW="240px" flexShrink={0} truncate>
      {name}
    </Text>
    <Text fontSize="xs" color="fg.subtle" flex="1">
      {detail}
    </Text>
    {resolution && (
      <Text
        fontSize="xs"
        fontFamily="mono"
        color={resolution.quiet ? 'fg.subtle' : 'fg.muted'}
        flexShrink={1}
        minW={0}
        maxW="40%"
        truncate
        title={resolution.detail}
      >
        {resolution.statement.replace(/^ → /, '→ ')}
        <VisuallyHidden>{`. ${resolution.detail}`}</VisuallyHidden>
      </Text>
    )}
    {mark && (
      /* Ghosted like the rest of the row and carrying no severity: it states a property of the
         destination, and a driver-supplied field an operator never touches is not a finding. */
      <Text fontSize="10px" color="fg.subtle" flexShrink={0} whiteSpace="nowrap" title={mark.detail}>
        {mark.label}
      </Text>
    )}
    {action}
  </HStack>
);
